import {
  DatagridConfigurable,
  List,
  SelectInput,
  EditButton,
  useRefresh,
  useNotify,
  TopToolbar,
  CreateButton,
  SelectColumnsButton,
  FilterButton,
  ExportButton,
} from 'react-admin';
import { useState, useCallback, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, Button } from '@mui/material';
import { Download, DownloadDone } from '@mui/icons-material';
import { AIPromptTagsCommandPanel } from './AIPromptTagsCommandPanel';
import {
  renderExportColumns,
  renderGridColumns,
} from '@/components/ConfigurableColumns';
import { columns as baseColumns } from './AIPromptsTagsColumns';
import { genericCSVExporter } from '@/util/genericCSVExporter';
import { AIPromptTagsEdit, AIPromptTagsCreate } from './AIPromptTagsEdit';

export const entityTypes = {
  journalEntries: 'Patient - Journal Entries',
  therapistSessionNotes: 'Therapist - Session Notes',
  therapistLiveNotes: 'Therapist - Live Notes',
  therapistHomework: 'Therapist - Homework',
  // Reused for document composer rulesets placeholders (aiPromptTags.assetType).
  documentComposerRulesets: 'Therapist - Document Composer Rulesets',
};

const statusChoices = [
  { id: 'enabled', name: 'Enabled' },
  { id: 'disabled', name: 'Disabled' },
];

export const entityTypesChoices = Object.entries(entityTypes).map(
  ([id, name]) => ({
    id,
    name,
  }),
);

const filters = [
  <SelectInput
    alwaysOn
    key="assetType"
    label="Entity Type"
    source="assetType"
    choices={entityTypesChoices}
  />,
  <SelectInput
    alwaysOn
    key="status"
    label="Status"
    source="status"
    choices={statusChoices}
  />,
];

const AIPromptTagsListActions = ({
  onCreate,
  exporter,
}: {
  onCreate: () => void;
  exporter: any;
}) => {
  const collectionName = 'aiPromptTags';
  const onGoToExports = useCallback(() => {
    const filterString = JSON.stringify({ collectionName });
    const filter = encodeURIComponent(filterString);
    location.href = `/#/dataExports?filter=${filter}`;
  }, [collectionName]);

  const [legacyExportEnabled, setLegacyExportEnabled] = useState(false);

  const onEnableLegacyExport = useCallback(() => {
    const confirmed = window.confirm(
      'Are you sure you want to enable legacy export? This mode will export based on your current filters.',
    );
    if (confirmed) {
      setLegacyExportEnabled(true);
    }
  }, []);

  const onConfirmLegacyExport = useCallback(async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLegacyExportEnabled(false);
  }, []);

  return (
    <TopToolbar>
      <Button
        variant="text"
        size="small"
        color="primary"
        onClick={onCreate}
        startIcon={<Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</Box>}
        sx={{ textTransform: 'uppercase', fontWeight: 500, fontSize: '0.8125rem' }}
      >
        Create
      </Button>
      <SelectColumnsButton />
      <FilterButton />
      <Button size="small" onClick={onGoToExports}>
        <DownloadDone />
        Exports
      </Button>
      {!legacyExportEnabled && (
        <Button size="small" onClick={onEnableLegacyExport}>
          <Download />
          Legacy Export
        </Button>
      )}
      {legacyExportEnabled && (
        <ExportButton
          color="warning"
          label="Confirm Legacy Export"
          maxResults={1000000}
          exporter={exporter}
          onClick={onConfirmLegacyExport}
        />
      )}
    </TopToolbar>
  );
};

export const AIPromptTagsList = () => {
  const [editId, setEditId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  const handleClose = useCallback(() => {
    setEditId(null);
    setCreateOpen(false);
  }, []);

  const handleEditSuccess = useCallback(() => {
    notify('Tag updated successfully');
    refresh();
    handleClose();
  }, [notify, refresh, handleClose]);

  const handleCreateSuccess = useCallback(() => {
    notify('Tag created successfully');
    refresh();
    handleClose();
  }, [notify, refresh, handleClose]);

  const ExpandPanel = useCallback(
    (props: any) => <AIPromptTagsCommandPanel {...props} onEdit={setEditId} />,
    [setEditId],
  );

  return (
    <>
      <List
        resource="aiPromptTags"
        filters={filters}
        actions={
          <AIPromptTagsListActions
            onCreate={() => setCreateOpen(true)}
            exporter={documentsCsvExporter}
          />
        }
        sort={{ field: 'entityName', order: 'ASC' }}
      >
        <DatagridConfigurable
          bulkActionButtons={false}
          expand={ExpandPanel}
        >
          {renderGridColumns(baseColumns)}
        </DatagridConfigurable>
      </List>

      <Dialog open={!!editId} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Edit Prompt Tag</DialogTitle>
        <DialogContent>
          {editId && (
            <AIPromptTagsEdit
              id={editId}
              onSuccess={handleEditSuccess}
              isDialog
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Create Prompt Tag</DialogTitle>
        <DialogContent>
          <AIPromptTagsCreate onSuccess={handleCreateSuccess} isDialog />
        </DialogContent>
      </Dialog>
    </>
  );
};

export const documentsCsvExporter = genericCSVExporter(
  'aiPromptTags',
  renderExportColumns(baseColumns),
);
